import { AfterViewInit, ChangeDetectionStrategy, Component, computed, effect, ElementRef, inject, OnDestroy, signal, ViewChild } from '@angular/core';
import { CommonModule } from '@angular/common';
import { ExportImportService } from '../../services/export-import.service';
import { CanvasService } from '../../services/canvas.service';
import { CANVAS_SIZES, CanvasSizeType } from '../../models/canvas-size.model';

@Component({
    selector: 'app-toolbar',
    imports: [CommonModule],
    templateUrl: './toolbar.component.html',
    styleUrl: './toolbar.component.scss',
    changeDetection: ChangeDetectionStrategy.OnPush
})
export class ToolbarComponent implements AfterViewInit, OnDestroy {
    @ViewChild('fileInput') fileInput!: ElementRef<HTMLInputElement>;
    @ViewChild('canvasSizeDropdown') canvasSizeDropdownRef!: ElementRef<HTMLDivElement>;

    readonly canvasService = inject(CanvasService);
    private exportImportService = inject(ExportImportService);
    private importMode: 'json' | 'image' | 'svg' = 'json';

    readonly canUndo = this.canvasService.canUndo;
    readonly canRedo = this.canvasService.canRedo;
    readonly canvasSizes = CANVAS_SIZES;
    readonly customSize = this.canvasService.canvasCustomSize;
    readonly isCustomSize = computed(() => this.canvasService.canvasSizeTypeValue() === 'custom');
    readonly customWidthInput = signal<string>('');
    readonly customHeightInput = signal<string>('');
    readonly isCanvasSizeMenuOpen = signal<boolean>(false);

    private readonly documentClickHandler = (event: MouseEvent) => {
        const target = event.target as Node | null;
        const dropdown = this.canvasSizeDropdownRef?.nativeElement;
        if (!dropdown || !target) return;
        if (!dropdown.contains(target)) {
            this.isCanvasSizeMenuOpen.set(false);
        }
    };

    constructor() {
        effect(() => {
            const size = this.customSize();
            this.customWidthInput.set(String(size.width));
            this.customHeightInput.set(String(size.height));
        });
    }

    ngAfterViewInit(): void {
        document.addEventListener('click', this.documentClickHandler);
    }

    ngOnDestroy(): void {
        document.removeEventListener('click', this.documentClickHandler);
    }

    getCurrentSizeName(): string {
        const currentType = this.canvasService.canvasSizeTypeValue();
        const size = CANVAS_SIZES.find(s => s.type === currentType);
        return size?.name || 'Custom';
    }

    setCanvasSize(type: CanvasSizeType): void {
        const currentType = this.canvasService.canvasSizeTypeValue();
        if (currentType === type) return;

        const hasObjects = this.canvasService.objects().length > 0;
        if (hasObjects) {
            const confirmed = confirm('Change canvas size? This will clear the design.');
            if (!confirmed) return;
            this.canvasService.clear();
        }

        if (type === 'custom') {
            const current = this.canvasService.canvasSize();
            this.canvasService.setCanvasCustomSize({ width: current.width, height: current.height });
            return;
        }
        this.canvasService.setCanvasSize(type);
        this.isCanvasSizeMenuOpen.set(false);
    }

    toggleCanvasSizeMenu(event: MouseEvent): void {
        event.stopPropagation();
        this.isCanvasSizeMenuOpen.update(open => !open);
    }

    keepCanvasSizeMenuOpen(event: MouseEvent): void {
        event.stopPropagation();
    }

    updateCustomSize(dimension: 'width' | 'height', event: Event): void {
        const inputValue = (event.target as HTMLInputElement).value;
        if (dimension === 'width') {
            this.customWidthInput.set(inputValue);
        } else {
            this.customHeightInput.set(inputValue);
        }

        if (inputValue.trim() === '') return;

        const value = Number.parseFloat(inputValue);
        if (!Number.isFinite(value)) return;
        const current = this.customSize();
        const next = {
            ...current,
            [dimension]: Math.max(1, Math.round(value))
        };
        this.canvasService.setCanvasCustomSize(next);
    }

    resetCustomSizeInput(dimension: 'width' | 'height'): void {
        const current = this.customSize();
        if (dimension === 'width') {
            this.customWidthInput.set(String(current.width));
        } else {
            this.customHeightInput.set(String(current.height));
        }
    }

    newCanvas(): void {
        if (confirm('Clear the design? This will delete all objects.')) {
            this.canvasService.clear();
        }
    }

    importJSON(): void {
        this.importMode = 'json';
        this.fileInput.nativeElement.accept = '.json';
        this.fileInput.nativeElement.click();
    }

    importImage(): void {
        this.importMode = 'image';
        this.fileInput.nativeElement.accept = 'image/*';
        this.fileInput.nativeElement.click();
    }

    importSVG(): void {
        this.importMode = 'svg';
        this.fileInput.nativeElement.accept = '.svg';
        this.fileInput.nativeElement.click();
    }

    async onFileSelected(event: Event): Promise<void> {
        const input = event.target as HTMLInputElement;
        const file = input.files?.[0];
        if (!file) return;

        try {
            if (this.importMode === 'json') {
                const text = await file.text();
                this.exportImportService.importFromJSON(text);
            } else if (this.importMode === 'image') {
                await this.exportImportService.importImage(file);
            } else if (this.importMode === 'svg') {
                await this.exportImportService.importSVG(file);
            }
        } catch (error) {
            console.error('Import failed:', error);
            alert('Failed to import file');
        } finally {
            // Reset file input
            input.value = '';
        }
    }

    exportJSON(): void {
        this.exportImportService.downloadJSON();
    }

    exportSVG(): void {
        this.exportImportService.downloadSVG();
    }

    exportPNG(): void {
        this.exportImportService.exportToPNG();
    }

    exportPDF(): void {
        this.exportImportService.exportToPDF().catch(err => {
            console.error('PDF export failed:', err);
            alert('Failed to export PDF');
        });
    }

    saveAndContinue(): void {
        try {
            // Get JSON and SVG strings
            const jsonStr = this.exportImportService.getExportJSON();
            const svgStr = this.exportImportService.exportToSVG();

            const payload = { json: JSON.parse(jsonStr), svg: svgStr };

            // If we are inside an iframe, post to the parent window (host page)
            if (window.parent && window.parent !== window) {
                let targetOrigin: string = '*';
                try {
                    // Prefer the referrer origin if available
                    if (document.referrer) targetOrigin = new URL(document.referrer).origin;
                } catch { /* keep wildcard origin if parsing fails */ }

                try {
                    window.parent.postMessage({ type: 'preview-data', payload }, targetOrigin);
                    history.back();
                    return;
                } catch (postErr) {
                    console.error('Failed to post message to parent window', postErr);
                    // fall through to attempting to find iframe element or open preview
                }
            }

            // // If a host page has an iframe for the canvas editor in the same document, post to it
            // const iframeId = 'idCanvasEditorIFramePath';
            // const hostIframe = document.getElementById(iframeId) as HTMLIFrameElement | null;
            // if (hostIframe && hostIframe.contentWindow) {
            //     let targetOrigin: string = '*';
            //     try {
            //         const src = hostIframe.getAttribute('src') || hostIframe.src || '';
            //         if (src) targetOrigin = new URL(src, window.location.href).origin;
            //     } catch { /* keep wildcard origin if parsing fails */ }
            //
            //     try {
            //         hostIframe.contentWindow.postMessage({ type: 'preview-data', payload }, targetOrigin);
            //         history.back();
            //         // alert('Sent canvas payload to iframe host');
            //         return;
            //     } catch (postErr) {
            //         console.error('Failed to post message to iframe', postErr);
            //         // fall through to opening preview window
            //     }
            // }
        } catch (err) {
            console.error('Save & Continue failed', err);
            alert('Failed to prepare preview');
        }
    }

    goBack(): void {
        try {
            history.back();
        } catch (err) {
            console.error('Back navigation failed', err);
        }
    }

}
